import { createContext, ReactNode, useContext, useMemo } from 'react'
import { TableColumn } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { AddRecordFormCell, RecordDetailTableCell } from './types'

interface CellPermissionsContextValue {
  isReadOnly: boolean
}

const CellPermissionsContext = createContext<CellPermissionsContextValue | null>(null)

interface RecordDetailsCellPermissionsProviderProps {
  children: ReactNode
  tableId: string
  column: Pick<TableColumn, 'id' | 'isReadOnly'>
  cell: RecordDetailTableCell | AddRecordFormCell
}

export function RecordDetailsCellPermissionsProvider({
  children,
  tableId,
  column,
  cell,
}: RecordDetailsCellPermissionsProviderProps) {
  const { permissionsManager } = useGlobalContext()

  // Determine if in edit or create mode based on if the cell has an id
  const mode = 'id' in cell ? 'edit' : 'create'

  const contextValue = useMemo(() => {
    const canEditCell = permissionsManager.canEditCell(tableId, column.id)

    let isReadOnly: boolean
    if (mode === 'edit') {
      // In edit mode, field should be disabled if:
      // - column is read only
      isReadOnly = column.isReadOnly || !canEditCell
    } else {
      // In create mode, field should be disabled if:
      // - user doesn't have permission to edit
      isReadOnly = !canEditCell
    }

    return { isReadOnly }
  }, [permissionsManager, tableId, column.id, column.isReadOnly, mode])

  return (
    <CellPermissionsContext.Provider value={contextValue}>
      {children}
    </CellPermissionsContext.Provider>
  )
}

export function useRecordDetailsCellPermissions() {
  const context = useContext(CellPermissionsContext)
  if (!context) {
    throw new Error(
      'useRecordDetailsCellPermissions must be used within RecordDetailsCellPermissionsProvider',
    )
  }
  return context
}
